import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import { keyframes } from '@emotion/react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { SiteLayout } from './home/SiteLayout';
import { useSiteTranslation } from './home/i18n';
import { StoreButtons } from './home/components/StoreButtons';
import { SERVER } from '../../Constants';

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const UserProfileComponent = () => {
  const { username } = useParams();
  const userAgent = useMemo(() => navigator.userAgent.toLowerCase(), []);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Holds a site translation key (profile.notFound / profile.loadFailed) so it follows the language.
  const [error, setError] = useState(null);
  const { t } = useSiteTranslation();
  // Field values come from the API in English; show the Arabic for known ones and the original otherwise.
  const translateValue = (value) => {
    const values = t('profileValues', { returnObjects: true }) || {};
    return values[String(value).trim().toLowerCase()] || value;
  };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t('profile.title', { name: username });
    return () => {
      document.title = previousTitle;
    };
  }, [t, username]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${SERVER}/auth/public/${username}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('profile.notFound');
          }
          throw new Error('profile.loadFailed');
        }
        
        const data = await response.json();
        setUserData(data?.publicData || data);
      } catch (err) {
        // Network failures throw TypeErrors whose messages aren't translation keys.
        setError(err.message.startsWith('profile.') ? err.message : 'profile.loadFailed');
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchUserData();
    }
  }, [username]);

  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);

  const normalizedData = useMemo(() => {
    if (!userData) return null;

    const profile = userData.profile || {};
    const firstName = (profile.firstName || '').trim();
    const countryName = typeof profile.country === 'string' ? profile.country : profile.country?.name;
    const interests = Array.isArray(profile.interests)
      ? profile.interests
          .map((interest) => (typeof interest === 'string' ? interest : interest?.title))
          .filter(Boolean)
      : [];

    return {
      username: userData.username || username,
      firstName,
      displayName: firstName || userData.username || username,
      avatarUrl: userData.picture?.miniUrl || userData.picture?.url || '',
      isVerified: Boolean(userData.isVerified || profile.isVerified),
      bio: profile.bio || '',
      city: profile.city || '',
      countryName: countryName || '',
      gender: profile.gender || '',
      maritalStatus: profile.maritalStatus || '',
      howReligious: profile.howReligious || '',
      sect: profile.sect || '',
      education: profile.education || '',
      work: profile.work || '',
      interests,
    };
  }, [userData, username]);

  const handleDownloadClick = () => {
    if (isIOS) {
      window.location.href = 'https://apps.apple.com/us/app/mjmarry/id6479207906';
    } else if (isAndroid) {
      window.location.href = 'https://play.google.com/store/apps/details?id=com.iahtasham.mjmarry';
    } else {
      // Desktop - show both options
      window.open('https://play.google.com/store/apps/details?id=com.iahtasham.mjmarry', '_blank');
    }
  };

  return (
    <SiteLayout noindex>
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #fffdf8 0%, #f5efe3 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Decorative background elements */}
      <Box sx={{
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'rgba(170, 130, 56, 0.08)',
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '-20%',
        left: '-5%',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'rgba(170, 130, 56, 0.06)',
        pointerEvents: 'none',
      }} />

      <Container maxWidth="md" sx={{ pt: { xs: 17, md: 22 }, pb: { xs: 6, md: 10 }, position: 'relative', zIndex: 1 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 5 }, 
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            //backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            animation: `${slideInUp} 0.8s ease-out`,
          }}
        >
          {loading && (
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                minHeight: { xs: 320, sm: 360 },
                animation: `${fadeIn} 0.5s ease-out`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Stack spacing={2} alignItems="center" justifyContent="center">
                <CircularProgress size={42} thickness={4.5} sx={{ color: '#aa8238' }} />
                <Typography sx={{ color: '#666', fontWeight: 500 }}>{t('profile.loading')}</Typography>
              </Stack>
            </Card>
          )}

          {error && (
            <Card elevation={0} sx={{ borderRadius: 3, p: 5, animation: `${fadeIn} 0.5s ease-out` }}>
              <Alert severity="error" sx={{ mb: 3 }}>{t(error)}</Alert>
              <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                {t('profile.missing')}
              </Typography>
              <Button
                variant="contained"
                component={Link}
                to="/"
                sx={{
                  background: 'linear-gradient(135deg, #aa8238 0%, #c7a45d 100%)',
                  borderRadius: 8,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: '700',
                }}
              >
                {t('profile.goHome')}
              </Button>
            </Card>
          )}

          {!loading && !error && userData && normalizedData && (
            <Card 
              elevation={0} 
              sx={{ 
                borderRadius: 3, 
                border: '1px solid rgba(170, 130, 56, 0.15)', 
                position: 'relative',
                overflow: 'hidden',
                minHeight: {md: 740, sm: 740, xs: 740},
                background: 'linear-gradient(135deg, #fffdf8 0%, #fbf8f1 100%)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: `${slideInUp} 1s ease-out 0.2s both`,
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Stack spacing={2.5} alignItems="center" sx={{ pointerEvents: 'none' }}>
                  <Box sx={{ position: 'relative' }}>
                    <Avatar 
                      src={normalizedData.avatarUrl} 
                      sx={{ 
                        width: 100, 
                        height: 100, 
                        bgcolor: '#aa8238',
                        fontSize: '2.5rem',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 24px rgba(170, 130, 56, 0.3)',
                      }}
                    >
                      {normalizedData.displayName?.[0]?.toUpperCase() || '@'}
                    </Avatar>
                    {normalizedData.isVerified && (
                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: '#aa8238',
                        borderRadius: '50%',
                        p: 0.5,
                        border: '2px solid white',
                      }}>
                        <VerifiedUserOutlinedIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                      </Box>
                    )}
                  </Box>

                  <Box textAlign="center">
                    <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: '-0.5px', mb: 0.5 }} p={0} m={0}>
                      {normalizedData.displayName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }} p={0} m={0}>
                      <bdi dir="ltr">@{normalizedData.username}</bdi>
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  spacing={1.5}
                  sx={{
                    width: '100%',
                    maxWidth: 420,
                    mx: 'auto',
                    mt: 3,
                    filter: 'blur(1px)',
                    opacity: 0.7,
                    pointerEvents: 'none',
                  }}
                >
                  {normalizedData.bio && (
                    <Typography variant="body2" sx={{ color: '#555', textAlign: 'center', fontStyle: 'italic' }}>
                      "{normalizedData.bio.slice(0, 80)}{normalizedData.bio.length > 80 ? '...' : ''}"
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" flexWrap="wrap">
                    {normalizedData.city && normalizedData.countryName && (
                      <Chip 
                        icon={<LocationOnOutlinedIcon />} 
                        label={`${normalizedData.city}, ${normalizedData.countryName}`}
                        size="small"
                        sx={{ bgcolor: '#f5ead0', color: '#6b5428' }}
                      />
                    )}
                    {normalizedData.gender && (
                      <Chip
                        label={translateValue(normalizedData.gender)}
                        size="small"
                        sx={{ bgcolor: '#f5ead0', color: '#6b5428' }}
                      />
                    )}
                    {normalizedData.maritalStatus && (
                      <Chip
                        label={translateValue(normalizedData.maritalStatus)}
                        size="small"
                        sx={{ bgcolor: '#f5ead0', color: '#6b5428' }}
                      />
                    )}
                    {normalizedData.howReligious && (
                      <Chip
                        label={translateValue(normalizedData.howReligious)}
                        size="small"
                        sx={{ bgcolor: '#f5ead0', color: '#6b5428' }}
                      />
                    )}
                    {normalizedData.sect && (
                      <Chip
                        label={translateValue(normalizedData.sect)}
                        size="small"
                        sx={{ bgcolor: '#f5ead0', color: '#6b5428' }}
                      />
                    )}
                    {normalizedData.education && (
                      <Chip 
                        icon={<SchoolOutlinedIcon />} 
                        label={translateValue(normalizedData.education)}
                        size="small"
                        sx={{ bgcolor: '#f5ead0', color: '#6b5428' }}
                      />
                    )}
                    {normalizedData.work && (
                      <Chip 
                        icon={<WorkOutlineIcon />} 
                        label={translateValue(normalizedData.work)}
                        size="small"
                        sx={{ bgcolor: '#f5ead0', color: '#6b5428' }}
                      />
                    )}
                  </Stack>

                  {normalizedData.interests.length > 0 && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#756f65', mb: 0.5, display: 'block' }}>{t('profile.interests')}</Typography>
                      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                        {normalizedData.interests.slice(0, 2).map((interest, idx) => (
                          <Chip 
                            key={idx}
                            label={interest}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: '#aa8238', color: '#aa8238', fontSize: '0.75rem' }}
                          />
                        ))}
                        {normalizedData.interests.length > 2 && (
                          <Chip 
                            label={`+${normalizedData.interests.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: '#aa8238', color: '#aa8238', fontSize: '0.75rem' }}
                          />
                        )}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </CardContent>

            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                top: { xs: '34%', sm: '38%', md: '40%' },
                background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.94) 18%, rgba(255,255,255,0.98) 100%)',
                borderRadius: '0 0 12px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                textAlign: 'center',
              }}
            >
              <Box sx={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)' }}>
                <Chip 
                  icon={<LockOutlinedIcon sx={{ fontSize: '1.2rem' }} />} 
                  label={t('profile.locked')}
                  variant="outlined"
                  sx={{
                    borderColor: '#aa8238',
                    color: '#aa8238',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    px: 1,
                  }}
                />
              </Box>

              <Stack spacing={3} alignItems="center" sx={{ width: '100%', maxWidth: 640, mt: { xs: 7, md: 8 } }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 500, mb: 1.5, color: '#211d18' }}  p={0} m={0}>
                    {t('profile.viewFull')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#666', fontSize: '1.05rem', lineHeight: 1.6 }}  p={0} m={0}>
                    {/* The name is user-provided, so it is rendered as text between two translated pieces. */}
                    {t('profile.previewBefore')}
                    <strong>{normalizedData?.displayName || username}</strong>
                    {t('profile.previewAfter')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#777', fontSize: '0.95rem', lineHeight: 1.6, mt: 0.75 }} p={0} m={0}>
                    {t('profile.viewMore')}
                  </Typography>
                </Box>

                <StoreButtons className="profile-store-row" />

                <Button
                  variant="contained"
                  onClick={handleDownloadClick}
                  sx={{
                    background: 'linear-gradient(135deg, #aa8238 0%, #c7a45d 100%)',
                    borderRadius: 8,
                    px: 5,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: '700',
                    fontSize: '1.05rem',
                    boxShadow: '0 8px 24px rgba(170, 130, 56, 0.35)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': { 
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(170, 130, 56, 0.45)',
                    },
                  }}
                >
                  {t('profile.downloadApp')}
                </Button>
              </Stack>
            </Box>
            </Card>
          )}

          <Box sx={{ mt: 6 }}>
            <Typography 
              variant="h5" 
              textAlign="center" 
              sx={{ 
                fontWeight: 500, 
                mb: 4,
                background: 'linear-gradient(135deg, #aa8238 0%, #c7a45d 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1.5rem', md: '1.75rem' },
              }}
               p={0} m={0}
            >
              {t('profile.joinTitle')}
            </Typography>
            
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3,
                    borderRadius: 2.5,
                    height: '100%',
                    border: '1.5px solid rgba(170, 130, 56, 0.2)',
                    background: 'linear-gradient(135deg, #fbf8f1 0%, #fff 100%)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    animation: `${slideInUp} 0.8s ease-out 0.3s both`,
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 16px 32px rgba(170, 130, 56, 0.15)',
                      borderColor: '#aa8238',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #aa8238 0%, #c7a45d 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 8px 16px rgba(170, 130, 56, 0.3)',
                    }}>
                      <VerifiedUserOutlinedIcon sx={{ color: 'white', fontSize: '1.8rem' }} />
                    </Box>
                    <Typography sx={{ fontWeight: '700', fontSize: '1.05rem', color: '#333' }} p={0} m={0}>
                      {t('profile.verified')}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3,
                    borderRadius: 2.5,
                    height: '100%',
                    border: '1.5px solid rgba(170, 130, 56, 0.2)',
                    background: 'linear-gradient(135deg, #fbf8f1 0%, #fff 100%)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    animation: `${slideInUp} 0.8s ease-out 0.4s both`,
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 16px 32px rgba(170, 130, 56, 0.15)',
                      borderColor: '#aa8238',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #aa8238 0%, #c7a45d 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 8px 16px rgba(170, 130, 56, 0.3)',
                    }}>
                      <FavoriteBorderOutlinedIcon sx={{ color: 'white', fontSize: '1.8rem' }} />
                    </Box>
                    <Typography sx={{ fontWeight: '700', fontSize: '1.05rem', color: '#333' }} p={0} m={0}>
                      {t('profile.matches')}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3,
                    borderRadius: 2.5,
                    height: '100%',
                    border: '1.5px solid rgba(170, 130, 56, 0.2)',
                    background: 'linear-gradient(135deg, #fbf8f1 0%, #fff 100%)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    animation: `${slideInUp} 0.8s ease-out 0.5s both`,
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 16px 32px rgba(170, 130, 56, 0.15)',
                      borderColor: '#aa8238',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #aa8238 0%, #c7a45d 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 8px 16px rgba(170, 130, 56, 0.3)',
                    }}>
                      <SecurityOutlinedIcon sx={{ color: 'white', fontSize: '1.8rem' }} />
                    </Box>
                    <Typography sx={{ fontWeight: '700', fontSize: '1.05rem', color: '#333' }} p={0} m={0}>
                      {t('profile.privacy')}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3,
                    borderRadius: 2.5,
                    height: '100%',
                    border: '1.5px solid rgba(170, 130, 56, 0.2)',
                    background: 'linear-gradient(135deg, #fbf8f1 0%, #fff 100%)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    animation: `${slideInUp} 0.8s ease-out 0.6s both`,
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 16px 32px rgba(170, 130, 56, 0.15)',
                      borderColor: '#aa8238',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #aa8238 0%, #c7a45d 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 8px 16px rgba(170, 130, 56, 0.3)',
                    }}>
                      <GroupsOutlinedIcon sx={{ color: 'white', fontSize: '1.8rem' }} />
                    </Box>
                    <Typography sx={{ fontWeight: '700', fontSize: '1.05rem', color: '#333' }}  p={0} m={0}>
                      {t('profile.community')}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
    </SiteLayout>
  );
};

export default UserProfileComponent;

